import type { CyclePhase, CycleProfile } from '../../lib/cycle'
import { getCyclePhase, getCyclePhaseSegments, getCycleProgress } from '../../lib/cycle'

const PHASE_COLORS: Record<CyclePhase, string> = {
  menstrual: '#f472b6',
  follicular: '#34d399',
  ovulatory: '#fbbf24',
  luteal: '#fb923c',
}

type CyclePhaseRingProps = {
  profile: CycleProfile
}

const CyclePhaseRing = ({ profile }: CyclePhaseRingProps) => {
  const segments = getCyclePhaseSegments(profile)
  const { day, progressPct } = getCycleProgress(profile)
  const phase = getCyclePhase(profile)
  const size = 200
  const stroke = 14
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const cycleLen = profile.avg_cycle_length

  let offset = 0
  const arcs = segments.map((seg) => {
    const span = (seg.endDay - seg.startDay + 1) / cycleLen
    const dash = span * circumference
    const arc = {
      phase: seg.phase,
      dash,
      gap: circumference - dash,
      offset: -offset,
      color: PHASE_COLORS[seg.phase],
      active: phase === seg.phase,
    }
    offset += dash
    return arc
  })

  const markerAngle = ((progressPct / 100) * 360 - 90) * (Math.PI / 180)
  const markerX = size / 2 + radius * Math.cos(markerAngle)
  const markerY = size / 2 + radius * Math.sin(markerAngle)

  return (
    <div className="relative mx-auto flex w-full max-w-[220px] flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="drop-shadow-sm">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-kilo-nested"
        />
        {arcs.map((arc) => (
          <circle
            key={arc.phase}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={arc.color}
            strokeWidth={stroke}
            strokeDasharray={`${arc.dash} ${arc.gap}`}
            strokeDashoffset={arc.offset}
            strokeLinecap="butt"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            opacity={arc.active ? 1 : 0.45}
          />
        ))}
        {day != null ? (
          <circle cx={markerX} cy={markerY} r={stroke / 2 + 2} fill="white" stroke="var(--kilo-accent)" strokeWidth={3} />
        ) : null}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pt-1">
        {day != null ? (
          <>
            <span className="text-3xl font-bold leading-none text-kilo-text">Day {day}</span>
            <span className="mt-1 text-xs text-kilo-soft">of {cycleLen}</span>
          </>
        ) : (
          <span className="px-4 text-center text-sm text-kilo-muted">Log your last period to see cycle day</span>
        )}
      </div>
    </div>
  )
}

export default CyclePhaseRing
